/**
 * Persistent, dismissible broker-connect nudge. Replaces the blocking
 * Pro-upgrade modal that used to interrupt broker connection.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import api from "@/lib/api";

const DISMISS_KEY = "ml_broker_banner_dismissed";

export default function BrokerConnectBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem(DISMISS_KEY)) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/broker/me");
        const linked = (data.connections || []).some((c) => c && !c.needs_relink);
        if (!cancelled && !linked) setShow(true);
      } catch {
        if (!cancelled) setShow(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setShow(false);
  };

  return (
    <div
      data-testid="broker-connect-banner"
      className="flex items-center gap-3 px-4 py-2 border-b text-xs"
      style={{
        background: "rgba(59,130,246,0.08)",
        borderColor: "rgba(59,130,246,0.28)",
        color: "var(--hoip-text-primary)",
      }}
    >
      <span className="flex-1" data-testid="broker-connect-banner-copy">
        Connect a broker for intraday option quotes and live pricing.
      </span>
      <Link
        to="/profile?tab=brokers"
        className="px-2 py-1 text-[10px] font-bold rounded-sm shrink-0"
        style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }}
        data-testid="broker-connect-banner-cta"
      >
        Open Profile
      </Link>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 opacity-70 hover:opacity-100"
        aria-label="Dismiss"
        data-testid="broker-connect-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
